import { dollarsToCents } from '@fruitful/fruitils';
import type { AccountSummary, DepositAccount } from '@fruitful/types';
import { isRetirementRegistrationType } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { InputNumber } from 'primereact/inputnumber';
import React, { useMemo, useState } from 'react';
import { ScrollView } from 'react-native';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Spinner,
    YStack,
} from 'smoothie';

import useGetAccountsForMembership from '../../../banking/banking/hooks/useGetAccountsForMembership';
import { ApexAccountLineItem } from '../../../banking/components/ApexAccountLineItem';
import SelectAccountSheet from '../../../banking/components/SelectAccountSheet';
import TransferAccountLineItem from '../../../banking/components/TransferAccountLineItem';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { captureException } from '../../../logger/Logger';
import { adminUserAtom } from '../../entityAtoms';
import type { MembershipDetails } from '../../entityTypes';
import useAdminICTDeposit, {
    type AdminICTDepositRequest,
    RetirementContributionType,
} from '../hooks/investments/useTestICTDeposit';

export const ApexDepositModal = ({
    account,
    membership,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    membership: MembershipDetails;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const adminUser = useAtomValue(adminUserAtom);

    const isRetirementAccount = useMemo(
        () => isRetirementRegistrationType(account.type),
        [account.type],
    );

    const [selectedSourceAccount, setSelectedSourceAccount] =
        useState<DepositAccount | null>(null);
    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [amount, setAmount] = useState(0);
    const [contributionType, setContributionType] = useState(
        RetirementContributionType.REGULAR,
    );
    const [taxYear, setTaxYear] = useState(new Date().getFullYear());
    const [result, setResult] = useState('');

    const { data: membershipAccounts, isPending } = useGetAccountsForMembership(
        {
            membershipId: membership.id,
        },
    );

    const { mutateAsync: submitDeposit, isPending: isSubmitting } =
        useAdminICTDeposit({});

    const mappedAccounts: DepositAccount[] = useMemo(() => {
        return (
            membershipAccounts
                ?.filter((acct) => {
                    return !acct.isExternal;
                })
                .map((acct) => {
                    return {
                        ...acct,
                        accountNumberMasked: '',
                        accountType: '',
                        bankName: '',
                        accountNumberMaskedIsTokenized: false,
                    };
                }) ?? []
        );
    }, [membershipAccounts]);

    const depositRequest: AdminICTDepositRequest = useMemo(() => {
        const base: AdminICTDepositRequest = {
            userId: account.userId ?? '',
            fromSyncteraAccountId: selectedSourceAccount?.id ?? '',
            toApexAccountId: account.id,
            amountCents: dollarsToCents(amount),
            memo: `Admin deposit initiated by ${adminUser.email}`,
        };
        if (isRetirementAccount) {
            base.retirementContribution = {
                taxYear,
                type: contributionType,
            };
        }
        return base;
    }, [
        selectedSourceAccount,
        contributionType,
        taxYear,
        amount,
        isRetirementAccount,
        adminUser.email,
    ]);

    const canSubmit = useMemo(() => {
        return (
            account.userId != null &&
            amount > 0 &&
            selectedSourceAccount != null
        );
    }, [account.userId, amount, selectedSourceAccount]);

    const isWaiting = useMemo(() => {
        return isPending || isSubmitting;
    }, [isPending, isSubmitting]);

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            renderInAppRoot={true}
        >
            <ScrollView>
                <YStack gap={32} padding={16} width={600} disabled={isWaiting}>
                    <BodyLargeEmphasized>Apex deposit</BodyLargeEmphasized>
                    {result !== '' && (
                        <YStack borderWidth={1} padding={16} gap={16}>
                            <BodyLargeEmphasized>
                                Deposit result
                            </BodyLargeEmphasized>
                            <BodyMediumRegular>{result}</BodyMediumRegular>
                        </YStack>
                    )}
                    <YStack gap={16}>
                        <BodySmallRegular>From</BodySmallRegular>
                        <TransferAccountLineItem
                            depositAccount={selectedSourceAccount}
                            placeholder="Select a bank account"
                            onPress={() => {
                                setShowAccountSelector(true);
                            }}
                            showActionIcon={true}
                        />
                    </YStack>
                    <YStack gap={16}>
                        <BodySmallRegular>To</BodySmallRegular>
                        <ApexAccountLineItem account={account} />
                    </YStack>
                    <YStack gap={16}>
                        <BodySmallRegular>Amount</BodySmallRegular>
                        <InputNumber
                            id="amount"
                            disabled={isWaiting}
                            value={amount}
                            onChange={(e) => {
                                setAmount(e.value ?? 0);
                            }}
                            mode="currency"
                            currency="USD"
                            min={0}
                        />
                    </YStack>
                    {isRetirementAccount && (
                        <YStack gap={16}>
                            <YStack>
                                <BodySmallRegular>
                                    Contribution type
                                </BodySmallRegular>
                                <select
                                    disabled={isWaiting}
                                    value={contributionType}
                                    onChange={(e) => {
                                        setContributionType(
                                            e.target
                                                .value as RetirementContributionType,
                                        );
                                    }}
                                    style={{
                                        padding: 12,
                                        borderRadius: 8,
                                        borderWidth: 1,
                                        borderColor: '#ccc',
                                        fontSize: 16,
                                    }}
                                >
                                    {Object.values(RetirementContributionType)
                                        .filter(
                                            (v) =>
                                                v !==
                                                RetirementContributionType.TYPE_UNSPECIFIED,
                                        )
                                        .map((v) => {
                                            return (
                                                <option
                                                    key={v.toString()}
                                                    value={v}
                                                >
                                                    {v.toString()}
                                                </option>
                                            );
                                        })}
                                </select>
                            </YStack>
                            <YStack>
                                <BodySmallRegular>Tax year</BodySmallRegular>
                                <InputNumber
                                    id="taxYear"
                                    disabled={isWaiting}
                                    value={taxYear}
                                    onChange={(e) => {
                                        setTaxYear(
                                            e.value ?? new Date().getFullYear(),
                                        );
                                    }}
                                    useGrouping={false}
                                    min={2020}
                                    max={new Date().getFullYear()}
                                />
                            </YStack>
                        </YStack>
                    )}
                    <SelectAccountSheet
                        isOpen={showAccountSelector}
                        depositAccounts={mappedAccounts}
                        onClose={() => {
                            setShowAccountSelector(false);
                        }}
                        onSelectAccount={(account) => {
                            setSelectedSourceAccount(account);
                        }}
                    />
                    <Button
                        icon={isWaiting ? <Spinner /> : null}
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={isWaiting ? '' : 'Submit deposit'}
                        onPress={async () => {
                            setResult('');
                            await submitDeposit(
                                {
                                    body: depositRequest,
                                },
                                {
                                    onSuccess: (body) => {
                                        const msg =
                                            body.stateMessage != null
                                                ? `${body.state}: ${body.stateMessage}`
                                                : body.state;
                                        setResult(msg);
                                    },
                                    onError: (error) => {
                                        captureException(error);
                                        setResult(error.message);
                                    },
                                },
                            );
                        }}
                        disabled={!canSubmit || isWaiting}
                    />
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
